import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/config.js';
import '../Projects/ProjectsList.css';
import './OutstandingList.css';

/**
 * Customer receivables table for the Overview Utestående tab.
 * @param {Object} props
 * @param {Array} props.customers
 * @param {string} props.sortColumn
 * @param {string} props.sortDirection
 * @param {function} props.onSortChange - Called with (column, direction)
 * @returns {JSX.Element}
 */
const OutstandingList = ({ customers, sortColumn, sortDirection, onSortChange }) => {
  const { t } = useTranslation();
  const locale = i18n.language === 'no' ? 'no-NO' : 'en-US';
  const [hover, setHover] = useState(null);

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    const kroner = Math.round(Number(value)) || 0;
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'NOK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(kroner);
  };

  const getSortValue = (customer, column) => {
    switch (column) {
      case 'customer_name':
        return customer.customer_name || '';
      case 'overdue_amount':
        return customer.overdue_amount ?? null;
      default:
        return null;
    }
  };

  const sortCustomers = (rows) => {
    if (!sortColumn) return rows;
    return [...rows].sort((a, b) => {
      const aValue = getSortValue(a, sortColumn);
      const bValue = getSortValue(b, sortColumn);
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;
      if (sortColumn === 'customer_name') {
        if (aValue === '') return 1;
        if (bValue === '') return -1;
      }
      let comparison = 0;
      if (aValue < bValue) comparison = -1;
      else if (aValue > bValue) comparison = 1;
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      onSortChange(column, sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      onSortChange(column, column === 'customer_name' ? 'asc' : 'desc');
    }
  };

  const getSortIndicator = (column) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const showHover = (event, customer) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const width = Math.min(880, Math.max(520, rect.width));
    let left = rect.left;
    if (left + width > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - width - 8);
    }
    const estimatedHeight = Math.min(280, 72 + ((customer.invoices || []).length * 32));
    let top = rect.bottom + 8;
    if (top + estimatedHeight > window.innerHeight - 8) {
      top = Math.max(8, rect.top - estimatedHeight - 8);
    }
    setHover({ customer, top, left, width });
  };

  const sortedInvoices = (invoices) => (
    [...(invoices || [])].sort((a, b) => {
      const aDate = a.invoice_due_date || '';
      const bDate = b.invoice_due_date || '';
      if (aDate === bDate) return 0;
      if (!aDate) return 1;
      if (!bDate) return -1;
      return aDate < bDate ? -1 : 1;
    })
  );

  if (!customers || customers.length === 0) {
    return (
      <div className="projects-list-empty">
        <p>{t('overview.receivablesNoData')}</p>
      </div>
    );
  }

  const sortedCustomers = sortCustomers(customers);
  const hoverCard = hover && createPortal(
    <div
      className="outstanding-invoice-hover"
      style={{ top: hover.top, left: hover.left, width: hover.width }}
    >
      <table className="outstanding-invoice-hover-table">
        <thead>
          <tr>
            <th>{t('overview.receivablesHover.invoiceNumber')}</th>
            <th>{t('overview.receivablesHover.daysOverdue')}</th>
            <th>{t('overview.receivablesHover.billed')}</th>
            <th>{t('overview.receivablesHover.credited')}</th>
            <th>{t('overview.receivablesHover.paid')}</th>
            <th>{t('overview.receivablesHover.remaining')}</th>
          </tr>
        </thead>
        <tbody>
          {sortedInvoices(hover.customer.invoices).map((invoice) => (
            <tr key={invoice.outbound_invoice_id}>
              <td>{invoice.invoice_number ?? '-'}</td>
              <td>{invoice.overdue ? invoice.days_overdue : ''}</td>
              <td>{formatCurrency(invoice.original_amount)}</td>
              <td>{formatCurrency(invoice.total_amount_credited)}</td>
              <td>{formatCurrency(Math.abs(invoice.total_amount_paid ?? 0))}</td>
              <td>{formatCurrency(invoice.remaining_amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>,
    document.body
  );

  return (
    <div className="outstanding-list-scroll">
      <div className="projects-list-container">
        <table className="projects-table outstanding-table">
          <thead>
            <tr>
              <th
                className="sortable-header outstanding-table-col-customer"
                onClick={() => handleSort('customer_name')}
              >
                <span className="table-header-content">
                  {t('overview.receivablesColumns.customer')}
                  <span className="sort-indicator">{getSortIndicator('customer_name')}</span>
                </span>
              </th>
              <th
                className="sortable-header projects-table-col-number"
                onClick={() => handleSort('overdue_amount')}
              >
                <span className="table-header-content">
                  {t('overview.receivablesColumns.overdue')}
                  <span className="sort-indicator">{getSortIndicator('overdue_amount')}</span>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedCustomers.map((customer) => (
              <tr
                key={customer.customer_org_number || customer.customer_name}
                className="projects-table-row outstanding-table-row"
                onMouseEnter={(event) => showHover(event, customer)}
                onMouseLeave={() => setHover(null)}
              >
                <td className="projects-table-col-customer outstanding-table-col-customer">
                  <span className="cell-line-clamp">{customer.customer_name || '-'}</span>
                </td>
                <td className="projects-table-col-number">{formatCurrency(customer.overdue_amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {hoverCard}
    </div>
  );
};

export default OutstandingList;
